import React, { useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import {
  FaExclamationTriangle, FaClock, FaBan,
  FaTools, FaMoneyBillWave, FaFireAlt, FaExclamationCircle,
  FaSpinner, FaSync, FaChartBar,FaPlus
} from 'react-icons/fa';
import '../styles/MaintenanceDashboard.css';
import IssueStatusStepper from './IssueStatusStepper';

const STATUS_COLORS = {
  pending: '#f39c12',
  accepted: '#3498db',
  in_progress: '#9b59b6',
  resolved: '#2ecc71',
  closed: '#1abc9c',
  rejected: '#e74c3c'
};
const SEVERITY_COLORS = { high: '#e74c3c', medium: '#f39c12', low: '#2ecc71' };

const BreakdownDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getBreakdownStats();
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      setError('Failed to load breakdown dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="maint-dash-loading">
        <FaSpinner className="spin" />
        <p>Loading Breakdown Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="maint-dash-error">
        <FaExclamationCircle />
        <p>{error}</p>
        <button onClick={fetchData}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, severity, issuesByDepartment, issuesByMonth, topAffectedAssets, recentIssues } = data;

  const statusPieData = [
    { name: 'Pending', value: summary.pendingIssues, color: STATUS_COLORS.pending },
    { name: 'Accepted', value: summary.acceptedIssues, color: STATUS_COLORS.accepted },
    { name: 'In Progress', value: summary.inProgressIssues, color: STATUS_COLORS.in_progress },
    { name: 'Resolved', value: summary.resolvedIssues, color: STATUS_COLORS.resolved },
    { name: 'Closed', value: summary.closedIssues, color: STATUS_COLORS.closed },
    { name: 'Rejected', value: summary.rejectedIssues, color: STATUS_COLORS.rejected }
  ].filter(d => d.value > 0);

  const severityPieData = [
    { name: 'High', value: severity.high, color: SEVERITY_COLORS.high },
    { name: 'Medium', value: severity.medium, color: SEVERITY_COLORS.medium },
    { name: 'Low', value: severity.low, color: SEVERITY_COLORS.low }
  ].filter(d => d.value > 0);

  const activeIssues = summary.pendingIssues + summary.acceptedIssues + summary.inProgressIssues;

  return (
    <div className="maint-dashboard breakdown-theme">
      {/* Header */}
      <div className="maint-dash-header">
        <div className="maint-dash-title">
          <FaExclamationTriangle className="header-icon breakdown-icon" />
          <div>
            <h1>Breakdown Maintenance</h1>
            <p>Real-time monitoring of breakdown issues and repairs</p>
          </div>
        </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-create-issue" onClick={() => window.location.href = '/issues/create'}>
              <FaPlus style={{ marginRight: 8 }} /> Create New Issue
            </button>
            <button className="btn-refresh" onClick={fetchData}>
              <FaSync /> Refresh
            </button>
          </div>
      </div>

      {/* Tab Navigation */}
      <div className="maint-dash-tabs">
        <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
          <FaChartBar /> Overview
        </button>
        <button className={activeTab === 'issues' ? 'active' : ''} onClick={() => setActiveTab('issues')}>
          <FaExclamationTriangle /> Recent Issues
        </button>
        <button className={activeTab === 'assets' ? 'active' : ''} onClick={() => setActiveTab('assets')}>
          <FaTools /> Affected Assets
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card danger">
              <div className="kpi-icon"><FaExclamationTriangle /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.totalIssues}</span>
                <span className="kpi-label">Total Breakdowns</span>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-icon"><FaClock /></div>
              <div className="kpi-content">
                <span className="kpi-value">{activeIssues}</span>
                <span className="kpi-label">Active Issues</span>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="kpi-icon"><FaFireAlt /></div>
              <div className="kpi-content">
                <span className="kpi-value">{severity.high}</span>
                <span className="kpi-label">High Severity</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-icon"><FaBan /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.rejectedIssues}</span>
                <span className="kpi-label">Rejected Issues</span>
              </div>
            </div>
            <div className="kpi-card purple">
              <div className="kpi-icon"><FaMoneyBillWave /></div>
              <div className="kpi-content">
                <span className="kpi-value">₹{summary.totalCost.toLocaleString()}</span>
                <span className="kpi-label">Total Repair Cost</span>
              </div>
            </div>
            <div className="kpi-card dark">
              <div className="kpi-icon"><FaExclamationCircle /></div>
              <div className="kpi-content">
                <span className="kpi-value">{summary.overdueLogs}</span>
                <span className="kpi-label">Overdue Tasks</span>
              </div>
            </div>
          </div>

          {/* Maintenance Log Summary */}
          <div className="log-summary-bar">
            <div className="log-summary-item">
              <span className="log-dot pending"></span>
              <span>Pending Logs: <strong>{summary.pendingLogs}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot in-progress"></span>
              <span>In Progress: <strong>{summary.inProgressLogs}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot completed"></span>
              <span>Completed: <strong>{summary.completedLogs}</strong></span>
            </div>
            <div className="log-summary-item">
              <span className="log-dot total"></span>
              <span>Total Logs: <strong>{summary.totalLogs}</strong></span>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-row">
            <div className="chart-card">
              <h3>Issue Status Distribution</h3>
              {statusPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {statusPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No issue data yet</p>}
            </div>

            <div className="chart-card">
              <h3>Severity Breakdown</h3>
              {severityPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={severityPieData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {severityPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No severity data yet</p>}
            </div>

            <div className="chart-card">
              <h3>Issues by Department</h3>
              {issuesByDepartment.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={issuesByDepartment}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#e74c3c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">No department data yet</p>}
            </div>
          </div>

          {/* Trend Chart */}
          <div className="chart-card full-width">
            <h3>Breakdown Trend (Last 6 Months)</h3>
            {issuesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={issuesByMonth}>
                  <defs>
                    <linearGradient id="breakdownGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e74c3c" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#e74c3c" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#e74c3c" fill="url(#breakdownGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="no-data">No trend data yet</p>}
          </div>
        </>
      )}

      {/* Recent Issues Tab */}
      {activeTab === 'issues' && (
        <div className="table-card">
          <h3>Recent Breakdown Issues</h3>
          {recentIssues.length > 0 ? (
            <div className="table-scroll">
              <table className="maint-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Lab</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Reported By</th>
                    <th>Technician</th>
                    <th>Reported At</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {recentIssues.map((issue) => (
                    <tr key={issue._id}>
                      <td className="td-asset">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontWeight: 700 }}>{issue.asset}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{issue.assetSerial || ''}</div>
                        </div>
                      </td>
                      <td>{issue.department}</td>
                      <td><span className={`badge severity-${issue.severity}`}>{issue.severity}</span></td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <span className={`badge status-${issue.status}`}>{issue.status.replace('_', ' ')}</span>
                          <IssueStatusStepper status={issue.status} />
                        </div>
                      </td>
                      <td>{issue.reportedBy}</td>
                      <td>{issue.technician}</td>
                      <td>{new Date(issue.reportedAt).toLocaleDateString()}</td>
                      <td className="td-desc">{issue.description?.substring(0, 60)}{issue.description?.length > 60 ? '...' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="no-data">No breakdown issues found</p>}
        </div>
      )}

      {/* Affected Assets Tab */}
      {activeTab === 'assets' && (
        <div className="table-card">
          <h3>Top Affected Assets</h3>
          {topAffectedAssets.length > 0 ? (
            <div className="assets-grid">
              {topAffectedAssets.map((asset, idx) => (
                <div key={idx} className="affected-asset-card">
                  <div className="asset-rank">#{idx + 1}</div>
                  <div className="asset-info">
                    <h4>{asset.name}</h4>
                    <p>{asset.department}</p>
                  </div>
                  <div className="asset-count">
                    <span className="count-value">{asset.count}</span>
                    <span className="count-label">issues</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="no-data">No asset data found</p>}
        </div>
      )}
    </div>
  );
};

export default BreakdownDashboard;
